import { Controller } from "react-hook-form";
import { FORM_ATTACHMENT_MIME_ALLOWLIST } from "@colab/shared";
import {
  FileAttachmentField,
  type FileAttachmentRow,
} from "@/components/shared/file-attachment-field";
import { useUploadFormAttachment } from "../use-upload-form-attachment";
import { useRemoveFormAttachment } from "../use-remove-form-attachment";
import { FieldShell, type FieldComponentProps } from "./field-shell";
import type { FormAttachment } from "../types";

// Sub-bucket 4 — refactored to consume the hoisted FileAttachmentField from
// `components/shared/`. The form-side specifics (RHF Controller wrap +
// FieldShell + draft-context guard) stay here; the visual + upload
// affordance moves to the shared component.
//
// Form-field schema's `multiple` doesn't exist (only `select` is multi);
// each file-field renders one attachment slot.

const MAX_BYTES = 25 * 1024 * 1024;

export function FileField({
  field,
  control,
  disabled,
  slug,
  submissionId,
}: FieldComponentProps) {
  if (field.kind !== "file") return null;
  if (!slug || !submissionId) {
    return (
      <FieldShell
        id={field.name}
        label={field.label}
        required={field.required}
        helpText={field.helpText}
      >
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
          Loading…
        </div>
      </FieldShell>
    );
  }

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: f, fieldState }) => (
        <FieldShell
          id={f.name}
          label={field.label}
          required={field.required}
          helpText={
            field.helpText ?? `Up to 25 MB · PDF, images, Word, Excel`
          }
          error={fieldState.error?.message}
        >
          <FileFieldBody
            slug={slug}
            submissionId={submissionId}
            disabled={disabled}
            value={(f.value as FormAttachment | null) ?? null}
            onChange={(v) => f.onChange(v)}
          />
        </FieldShell>
      )}
    />
  );
}

function FileFieldBody({
  slug,
  submissionId,
  value,
  onChange,
  disabled,
}: {
  slug: string;
  submissionId: string;
  value: FormAttachment | null;
  onChange: (v: FormAttachment | null) => void;
  disabled?: boolean;
}) {
  const upload = useUploadFormAttachment(slug, submissionId);
  const remove = useRemoveFormAttachment(slug);

  const attachments: FileAttachmentRow[] = value
    ? [
        {
          id: value.id,
          file_name: value.file_name,
          size_bytes: value.size_bytes,
          content_type: value.content_type,
        },
      ]
    : [];

  return (
    <FileAttachmentField
      label={null} // FieldShell already renders the label
      helpText={null}
      attachments={attachments}
      multiple={false}
      maxSizeBytes={MAX_BYTES}
      acceptedMimeTypes={FORM_ATTACHMENT_MIME_ALLOWLIST}
      disabled={disabled}
      onUpload={async (file) => {
        const result = await upload.mutateAsync(file);
        // Update RHF value so submit references the persisted attachment id.
        // The page-level detail refetch will deliver the canonical row; this
        // optimistic stamp keeps the input responsive in the interim.
        const row: FormAttachment = {
          id: result.id,
          submission_id: submissionId,
          partner_id: "",
          storage_key: "",
          file_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          uploaded_by: null,
          created_at: new Date().toISOString(),
        };
        onChange(row);
        return {
          id: row.id,
          file_name: row.file_name,
          size_bytes: row.size_bytes,
          content_type: row.content_type,
        };
      }}
      onRemove={async (id) => {
        await remove.mutateAsync({ id });
        onChange(null);
      }}
    />
  );
}
